import { useUserInfo } from '@/features/_data/useUserInfo';
import { IconChevronDown, IconChevronUp } from '@/icons';
import { Button } from '@/ui/Button/Button';
import EditTextModal from '@/ui/EditTextModal/EditTextModal';
import { Flex } from '@/ui/Flex/Flex';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { sortByIndexOptional } from '@/utils/arrayUtils';
import { FrontendSlide } from '@/types/SlideType';
import { BaseSlideGroupProps } from '../../../../../../../types/SlideGroupProps';
import { useProductConfig } from '../../../../../_data/useProductConfig';
import { EditorSlideComponent } from '../../../EditorSlideComponent';
import { useSlideGroupBase } from '../../hooks/useSlideGroupBase';
import {
  StyledCard,
  StyledCardContentsWrapper,
  StyledExpandButtonWrapper,
  StyledSlideGroup,
} from '../../slidegroup-style';
import { StyledGenericWrapper } from './generic-styles';
import SlideGroupKebapMenu from '../../SlideGroupKebapMenu';

export const GenericSlideGroup: React.FC<BaseSlideGroupProps> = (props) => {
  const {
    isExpanded,
    setIsExpanded,
    handleDeleteGroup,
    handleMoveUp,
    handleMoveDown,
  } = useSlideGroupBase(props);

  const {
    unitId,
    slideGroup,
    disallowSlideDeletion,
    isFirstGroup,
    isLastGroup,
    channelSelective,
    languageSupport,
    images,
    setImages,
    videos,
    setVideos,
    webslides,
    setWebslides,
    magicModels,
    onClick,
    onUpdateSlide,
    onUpdateSlideGroupName,
    onUpdateSlideOrder,
    onVideoOptionsChange,
    onChangeNameInLibrary,
  } = props;
  const { config } = useProductConfig(unitId);
  const { isAdmin } = useUserInfo();
  const slides: (FrontendSlide | null)[] = [...Object.values(slideGroup.slides)]
    .sort(sortByIndexOptional)
    .slice(0, config.maxSlidesPerGroup);

  const updateSlideGroupName = (inputValue: string) => {
    onUpdateSlideGroupName(slideGroup.id, inputValue);
  };

  // add a null to add the conditional slide in the end
  if (slides.length < config.maxSlidesPerGroup) slides.push(null);

  return (
    <StyledGenericWrapper>
      <StyledSlideGroup>
        <StyledCard
          isExpanded={isExpanded}
          title={
            <Flex align="center" gap={12}>
              <EditTextModal
                title="Edit Slide Group Name"
                initialValue={slideGroup.name}
                onSave={updateSlideGroupName}
              />
            </Flex>
          }
        >
          <StyledCardContentsWrapper
            maxSlidesPerGroup={config.maxSlidesPerGroup}
          >
            {Array.isArray(slides) &&
              slides.map((slide, slideIndex, slideArray) => {
                const slideKey = slide === null ? 'no-slide' : slide.slideId;
                const isFirst = slideIndex === 0;
                const isLast = slideIndex === slideArray.length - 1;
                return (
                  <EditorSlideComponent
                    key={slideKey}
                    slide={slide}
                    slideGroupId={slideGroup.id}
                    slidePosition={slides.length}
                    magicModels={magicModels}
                    languageSupport={languageSupport}
                    disallowSlideDeletion={disallowSlideDeletion}
                    channelSelective={channelSelective}
                    isFirst={isFirst}
                    isLast={isLast}
                    images={images}
                    videos={videos}
                    webslides={webslides}
                    setImages={setImages}
                    setVideos={setVideos}
                    setWebslides={setWebslides}
                    onClick={onClick}
                    onChangeNameInLibrary={onChangeNameInLibrary}
                    onUpdateSlide={onUpdateSlide}
                    onUpdateSlideOrder={onUpdateSlideOrder}
                    onVideoOptionsChange={onVideoOptionsChange}
                  />
                );
              })}
            {slides.length >= 4 && (
              <StyledExpandButtonWrapper isExpanded={isExpanded}>
                <Button
                  mode="outline"
                  onClick={() => setIsExpanded((prev) => !prev)}
                >
                  <Flex align="center" gap={8}>
                    {isExpanded ? <IconChevronUp /> : <IconChevronDown />}
                    {isExpanded ? (
                      <FormattedMessage defaultMessage="See less" id="GZo7vg" />
                    ) : (
                      <FormattedMessage defaultMessage="Expand" id="0oLj/t" />
                    )}
                  </Flex>
                </Button>
              </StyledExpandButtonWrapper>
            )}
          </StyledCardContentsWrapper>
          <div
            style={{
              position: 'absolute',
              zIndex: '10',
              top: '16px',
              right: '16px',
            }}
          >
            <SlideGroupKebapMenu
              handleMoveUp={handleMoveUp}
              handleMoveDown={handleMoveDown}
              handleDeleteGroup={handleDeleteGroup}
              isLastGroup={isLastGroup}
              isFirstGroup={isFirstGroup}
              isAdmin={isAdmin}
              disallowSlideDeletion={disallowSlideDeletion}
            />
          </div>
        </StyledCard>
      </StyledSlideGroup>
    </StyledGenericWrapper>
  );
};
